import React, { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Provide fallback values when context is not available
    return {
      isAuthenticated: false,
      user: null,
      login: () => {},
      logout: () => {},
      lastAttemptedRoute: '/',
      setRouteBeforeLogin: () => {},
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {

  const SERVER_URL = process.env.REACT_APP_API_BASE_URL;
  
  // Auth endpoints don't use the /api prefix, so we need to remove it if present
  const AUTH_SERVER_URL = SERVER_URL.replace('/api/v1', '');
  
  // Debug logging for auth configuration
  console.log('Auth Debug - SERVER_URL:', SERVER_URL);
  console.log('Auth Debug - AUTH_SERVER_URL:', AUTH_SERVER_URL);


  // Components which use the AuthContext check whether isAuthenticated is null, true, or false.
  // If it's null, they can display a loading spinner or some other loading indicator. If it's true, they render the protected component. If it's false, they redirect to the login page.
  // Redirect to login should NOT occur if isAuthenticated is null, because we don't yet know whether the user is authenticated or not.
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null indicates loading state
  const [user, setUser] = useState(null);

  // Default to '/get-started' if no route was attempted before login
  const [lastAttemptedRoute, setLastAttemptedRoute] = useState('/get-started');

  // Extract validateToken function so it can be called from login
  const validateToken = async () => {
    try {
      console.log('Validating token with server...');
      
      // Check for token in localStorage first
      const token = localStorage.getItem('access_token');
      
      if (token) {
        // Case 1: Token found in localStorage (normal login flow)
        console.log('Token found in localStorage');
        
        // Set Authorization header if not already set
        if (!axios.defaults.headers.common['Authorization']) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await axios.get(`${AUTH_SERVER_URL}/auth/validate-token`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        console.log('Received a response from token validation request');
        console.log('Response:', response);
        console.log('Response data:', response.data);
        console.log('Response.data.valid:', response.data?.valid);

        // Handle both possible response structures
        const responseData = response.data || response;
        
        if (responseData.valid) {
          setIsAuthenticated(true);
          setUser(responseData.user);
          console.log('Token validated by server');
        } else {
          setIsAuthenticated(false);
          setUser(null);
          console.log('Token is not valid');
        }
      } else {
        // Case 2: No token in localStorage, try cookie authentication (Azure AD SSO flow)
        console.log('No token found in localStorage, trying cookie authentication...');
        
        // Clear any existing Authorization header
        delete axios.defaults.headers.common['Authorization'];
        
        const response = await axios.get(`${AUTH_SERVER_URL}/auth/validate-token`, {
          withCredentials: true  // This ensures cookies are sent
        });

        console.log('Received a response from cookie validation request');
        console.log('Response:', response);
        console.log('Response data:', response.data);

        const responseData = response.data || response;
        
        if (responseData.valid) {
          setIsAuthenticated(true);
          setUser(responseData.user);
          console.log('Cookie authentication successful');
        } else {
          setIsAuthenticated(false);
          setUser(null);
          console.log('Cookie authentication failed');
        }
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      // Clear invalid token if it exists
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // js can't read using cookies.get because of xss
  // So we rely on withCredentials to send the cookie to the server
  useEffect(() => {
    validateToken();
  }, []);

  const login = (token = null) => {
    console.log('Login called with token:', token ? 'provided' : 'not provided');
    
    if (token) {
      // Store token in localStorage and set Authorization header
      localStorage.setItem('access_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Token stored in localStorage and Authorization header set');
    }
    
    // Immediately validate token to get user data
    validateToken();
  };



  const logout = async () => {
    // Clear localStorage token and Authorization header
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    
    // Also try to call backend logout endpoint (for httpOnly cookie cleanup if any)
    try {
      await axios.post(`${AUTH_SERVER_URL}/auth/logout`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with client-side cleanup even if server call fails
    }
    
    // Clean up any other cookies (fallback)
    Cookies.remove('access_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  const setRouteBeforeLogin = (route) => {
    setLastAttemptedRoute(route);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout, 
      lastAttemptedRoute, 
      setRouteBeforeLogin 
    }}>
      {children}
    </AuthContext.Provider>
  );
};